import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, Table } from "reactstrap";
import sizingGridCsvUrl from "../../assets/csv/sizing_grid.csv";

function parseCsvLine(line) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];

    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }

  cells.push(current.trim());

  return cells;
}

function parseCsvTable(csvText) {
  const lines = String(csvText ?? "")
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");

  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = parseCsvLine(lines[0]);

  const rows = lines.slice(1).map((line) => {
    const values = parseCsvLine(line);
    return headers.map((_, idx) => values[idx] ?? "");
  });

  return { headers, rows };
}

function parseDimensionValues(raw, isNonStandard) {
  if (raw == null || String(raw).trim() === "") return [];

  return String(raw)
    .split(",")
    .map((entry) => String(entry).trim())
    .filter(Boolean)
    .map((text) => {
      const num = Number(text);
      return {
        text,
        numericValue: Number.isNaN(num) ? null : num,
        isNonStandard,
      };
    });
}

function sortDimensionValues(values) {
  return values.slice().sort((a, b) => {
    const aNum = a.numericValue;
    const bNum = b.numericValue;

    if (aNum != null && bNum != null) return aNum - bNum;
    if (aNum != null) return -1;
    if (bNum != null) return 1;

    return a.text.localeCompare(b.text);
  });
}

function toSizingRows(parsed) {
  const headers = Array.isArray(parsed?.headers) ? parsed.headers : [];
  const csvRows = Array.isArray(parsed?.rows) ? parsed.rows : [];

  if (headers.length === 0 || csvRows.length === 0) return [];

  const byKey = (row, key) => {
    const idx = headers.indexOf(key);
    return idx === -1 ? "" : row[idx] ?? "";
  };

  return csvRows.map((row) => {
    const category = String(byKey(row, "category")).trim();
    const heightValues = sortDimensionValues([
      ...parseDimensionValues(byKey(row, "height_s"), false),
      ...parseDimensionValues(byKey(row, "height_ns"), true),
    ]);
    const widthValues = sortDimensionValues([
      ...parseDimensionValues(byKey(row, "width_s"), false),
      ...parseDimensionValues(byKey(row, "width_ns"), true),
    ]);
    const depthValues = sortDimensionValues([
      ...parseDimensionValues(byKey(row, "depth_s"), false),
      ...parseDimensionValues(byKey(row, "depth_ns"), true),
    ]);

    return {
      category,
      heightValues,
      widthValues,
      depthValues,
    };
  });
}

const SizingGrid = ({ isOpen = false, setIsOpen }) => {
  const [sizingRows, setSizingRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [didLoad, setDidLoad] = useState(false);

  const setModalOpen = useCallback(
    (nextOpen) => {
      if (typeof setIsOpen === "function") setIsOpen(nextOpen);
    },
    [setIsOpen]
  );

  const loadSizingGrid = useCallback(async () => {
    if (didLoad || isLoading) return;

    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(sizingGridCsvUrl);

      if (!res.ok) throw new Error("Unable to load sizing grid.");

      const text = await res.text();
      const parsed = parseCsvTable(text);
      setSizingRows(toSizingRows(parsed));
      setDidLoad(true);
    } catch (e) {
      setError(e?.message || "Failed to parse sizing grid.");
    } finally {
      setIsLoading(false);
    }
  }, [didLoad, isLoading]);

  useEffect(() => {
    if (!isOpen) return;
    loadSizingGrid();
  }, [isOpen, loadSizingGrid]);

  const renderDimensionValues = (values) => {
    if (!Array.isArray(values) || values.length === 0) return null;

    return values.map((entry, idx) => (
      <React.Fragment key={`${entry.text}-${idx}`}>
        <span className={entry.isNonStandard ? "text-muted" : ""}>
          {entry.text}
        </span>
        {idx < values.length - 1 ? ", " : null}
      </React.Fragment>
    ));
  };

  const sectionGroups = useMemo(
    () => [
      {
        title: "Heights",
        key: "heightValues",
      },
      {
        title: "Widths",
        key: "widthValues",
      },
      {
        title: "Depths",
        key: "depthValues",
      },
    ],
    []
  );

  const sectionRows = useMemo(
    () =>
      sectionGroups
        .map((section) => {
          const rows = sizingRows
            .map((row, idx) => ({
              key: `${section.key}-${idx}-${row.category}`,
              category: row.category,
              values: Array.isArray(row[section.key]) ? row[section.key] : [],
            }))
            .filter((row) => row.values.length > 0);
          return { ...section, rows };
        })
        .filter((section) => section.rows.length > 0),
    [sectionGroups, sizingRows]
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={() => setModalOpen(false)}
        size="xl"
        centered
      >
        <ModalHeader toggle={() => setModalOpen(false)}>
          <div>Sizing Grid</div>
          <small className="fs-6 text-muted fw-normal">All measurements in mm</small>
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <div className="text-muted">Loading sizing grid...</div>
          ) : null}
          {!isLoading && error ? (
            <div className="alert alert-danger py-2 mb-0">{error}</div>
          ) : null}
          {!isLoading && !error && sizingRows.length > 0 ? (
            <div>
              {sectionRows.map((section) => (
                <div key={section.key} className="mb-4">
                  <div className="bg-secondary text-white px-2 py-1 rounded fw-semibold mb-2">
                    {section.title}
                  </div>
                  <Table bordered size="sm" className="mb-0">
                    <tbody>
                      {section.rows.map((row) => (
                        <tr key={row.key}>
                          <td className="fw-semibold" style={{ width: "35%" }}>
                            {row.category}
                          </td>
                          <td>{renderDimensionValues(row.values)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ))}
            </div>
          ) : null}
          {!isLoading && !error && sizingRows.length === 0 ? (
            <div className="text-muted">No sizing grid data available.</div>
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
};

export default SizingGrid;
